import { useEffect, useState, type FormEvent } from "react";

import type {
  Attendee,
  AttendeeSource,
  AttendeeStatus,
  Event
} from "@attendik/types/admin-schemas";

import { api, type AttendeeSortColumn } from "../../api/client.js";
import { isApiError } from "../../api/errors.js";
import { Button } from "../../components/ui/button.js";
import { Card } from "../../components/ui/card.js";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader
} from "../../components/ui/dialog.js";
import { Input } from "../../components/ui/input.js";
import { Label } from "../../components/ui/label.js";
import { Select } from "../../components/ui/select.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../components/ui/table.js";
import { EmptyState, ErrorState, LoadingState } from "../../components/states.js";
import { useAsync } from "../../lib/useAsync.js";
import {
  SourcePill,
  StatusBadge,
  attendeeStatusVariant
} from "../shared/pills.js";

const PAGE_SIZE = 25;

/** A clickable column header that drives the single active server-side sort. */
function SortableHeader({
  column,
  label,
  activeSort,
  activeDir,
  onSort
}: {
  column: AttendeeSortColumn;
  label: string;
  activeSort: AttendeeSortColumn;
  activeDir: "asc" | "desc";
  onSort: (column: AttendeeSortColumn) => void;
}): JSX.Element {
  const active = activeSort === column;
  return (
    <TableHead
      aria-sort={
        active ? (activeDir === "asc" ? "ascending" : "descending") : "none"
      }
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-foreground"
      >
        {label}
        <span aria-hidden="true" className="text-[0.65rem]">
          {active ? (activeDir === "asc" ? "▲" : "▼") : ""}
        </span>
      </button>
    </TableHead>
  );
}

/**
 * Attendees tab — filterable list, manual CRUD, sync trigger (build plan
 * P2-T8c).
 */
export function AttendeesTab({ event }: { event: Event }): JSX.Element {
  const [sourceFilter, setSourceFilter] = useState<AttendeeSource | "">("");
  const [statusFilter, setStatusFilter] = useState<AttendeeStatus | "">("");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sort, setSort] = useState<AttendeeSortColumn>("name");
  const [dir, setDir] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(1);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Attendee | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Debounce the search box (300ms). No shared debounce util exists; this is the
  // established inline pattern. Resets to page 1 whenever the query changes.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const attendees = useAsync(
    () =>
      api.listAttendees(event.id, {
        source: sourceFilter || undefined,
        status: statusFilter || undefined,
        q: debouncedSearch || undefined,
        sort,
        dir,
        page,
        limit: PAGE_SIZE
      }),
    [event.id, sourceFilter, statusFilter, debouncedSearch, sort, dir, page]
  );

  // One active sort at a time: clicking the active column flips direction,
  // clicking another column selects it ascending. Always resets to page 1.
  const handleSort = (column: AttendeeSortColumn): void => {
    if (sort === column) {
      setDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSort(column);
      setDir("asc");
    }
    setPage(1);
  };

  const meetpoint = useAsync(async () => {
    try {
      return await api.getMeetpointConfig(event.id);
    } catch (err) {
      if (isApiError(err) && err.status === 404) {
        return null;
      }
      throw err;
    }
  }, [event.id]);

  const [syncing, setSyncing] = useState(false);

  const handleSync = async (): Promise<void> => {
    setActionError(null);
    setSyncing(true);
    try {
      const { sync_log_id } = await api.triggerSync(event.id);
      // Poll the sync run to completion.
      for (let i = 0; i < 30; i += 1) {
        const run = await api.getSyncRun(sync_log_id);
        if (run.status !== "RUNNING") {
          break;
        }
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
      attendees.reload();
      meetpoint.reload();
    } catch (err) {
      if (isApiError(err) && err.code === "SYNC_IN_PROGRESS") {
        setActionError("A sync is already running for this event.");
      } else {
        setActionError(isApiError(err) ? err.message : "Sync failed.");
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleDelete = async (attendee: Attendee): Promise<void> => {
    setActionError(null);
    try {
      await api.deleteAttendee(event.id, attendee.id);
      attendees.reload();
    } catch (err) {
      if (isApiError(err) && err.code === "HAS_SCANS") {
        setActionError(
          `${attendee.name} has scan history and cannot be deleted.`
        );
      } else {
        setActionError(isApiError(err) ? err.message : "Could not delete.");
      }
    }
  };

  const totalPages = attendees.data
    ? Math.max(1, Math.ceil(attendees.data.total / PAGE_SIZE))
    : 1;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Label htmlFor="att-search" className="text-muted-foreground">
              Search
            </Label>
            <Input
              id="att-search"
              type="search"
              className="w-60"
              placeholder="Search name, email, company"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label="Search attendees by name, email, or company"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="att-source" className="text-muted-foreground">
              Source
            </Label>
            <Select
              id="att-source"
              className="w-36"
              value={sourceFilter}
              onChange={(e) => {
                setSourceFilter(e.target.value as AttendeeSource | "");
                setPage(1);
              }}
            >
              <option value="">All</option>
              <option value="manual">Manual</option>
              <option value="meetpoint">Meetpoint</option>
              <option value="csv">CSV</option>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="att-status" className="text-muted-foreground">
              Status
            </Label>
            <Select
              id="att-status"
              className="w-40"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as AttendeeStatus | "");
                setPage(1);
              }}
            >
              <option value="">All</option>
              <option value="IN_ROOM">In room</option>
              <option value="OUT_OF_ROOM">Out of room</option>
              <option value="NOT_SCANNED">Not scanned</option>
            </Select>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleSync}
            disabled={syncing || !meetpoint.data}
            title={
              meetpoint.data
                ? "Pull registered participants from Meetpoint"
                : "Configure the Meetpoint integration first"
            }
          >
            {syncing ? "Syncing…" : "Sync from Meetpoint"}
          </Button>
          <Button onClick={() => setAdding(true)}>Add attendee</Button>
        </div>
      </div>

      {meetpoint.data ? (
        <p className="text-xs text-muted-foreground">
          Last Meetpoint sync:{" "}
          {meetpoint.data.last_synced_at
            ? `${meetpoint.data.last_synced_at} (${meetpoint.data.last_sync_status ?? "—"})`
            : "never"}
        </p>
      ) : null}

      {actionError ? (
        <p role="alert" className="text-sm text-destructive">
          {actionError}
        </p>
      ) : null}

      <Card>
        {attendees.loading ? (
          <LoadingState />
        ) : attendees.error ? (
          <ErrorState message={attendees.error} onRetry={attendees.reload} />
        ) : !attendees.data || attendees.data.data.length === 0 ? (
          <EmptyState
            title="No attendees"
            description="Add attendees manually or sync them from Meetpoint."
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHeader
                    column="name"
                    label="Name"
                    activeSort={sort}
                    activeDir={dir}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    column="email"
                    label="Email"
                    activeSort={sort}
                    activeDir={dir}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    column="company"
                    label="Company"
                    activeSort={sort}
                    activeDir={dir}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    column="source"
                    label="Source"
                    activeSort={sort}
                    activeDir={dir}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    column="status"
                    label="Status"
                    activeSort={sort}
                    activeDir={dir}
                    onSort={handleSort}
                  />
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {attendees.data.data.map((attendee) => (
                  <TableRow key={attendee.id}>
                    <TableCell className="font-medium">{attendee.name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {attendee.email ?? "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {attendee.company ?? "—"}
                    </TableCell>
                    <TableCell>
                      <SourcePill source={attendee.source} />
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        variant={attendeeStatusVariant(attendee.status)}
                      >
                        {attendee.status}
                      </StatusBadge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditing(attendee)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(attendee)}
                        >
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex items-center justify-between border-t border-border p-3 text-sm">
              <span className="text-muted-foreground">
                {attendees.data.total} attendee
                {attendees.data.total === 1 ? "" : "s"}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <span className="text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>

      {adding ? (
        <AttendeeFormDialog
          eventId={event.id}
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            attendees.reload();
          }}
        />
      ) : null}
      {editing ? (
        <AttendeeFormDialog
          eventId={event.id}
          attendee={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            attendees.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function AttendeeFormDialog({
  eventId,
  attendee,
  onClose,
  onSaved
}: {
  eventId: string;
  attendee?: Attendee;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const [name, setName] = useState(attendee?.name ?? "");
  const [email, setEmail] = useState(attendee?.email ?? "");
  const [company, setCompany] = useState(attendee?.company ?? "");
  // CR-07. Comma-separated input — the simplest UX for a 0..N list that
  // also handles paste from MP's group label text. Trim / drop empties /
  // dedup happens server-side; the form just splits.
  const [groupNamesText, setGroupNamesText] = useState(
    (attendee?.group_names ?? []).join(", ")
  );
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const parseGroupNames = (text: string): string[] =>
    text
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const groupNames = parseGroupNames(groupNamesText);
      if (attendee) {
        await api.updateAttendee(eventId, attendee.id, {
          name: name.trim(),
          email: email.trim() || null,
          company: company.trim() || null,
          group_names: groupNames
        });
      } else {
        await api.createAttendee(eventId, {
          name: name.trim(),
          email: email.trim() || null,
          company: company.trim() || null,
          group_names: groupNames
        });
      }
      onSaved();
    } catch (err) {
      setError(isApiError(err) ? err.message : "Could not save the attendee.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={onClose} labelledBy="attendee-form-title">
      <DialogHeader
        id="attendee-form-title"
        title={attendee ? "Edit attendee" : "Add attendee"}
        description={
          attendee
            ? "Update this attendee's details."
            : "A unique badge ID is generated automatically."
        }
        onClose={onClose}
      />
      <form onSubmit={handleSubmit}>
        <DialogBody>
          <div className="space-y-1.5">
            <Label htmlFor="att-name">Name</Label>
            <Input
              id="att-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="att-email">Email (optional)</Label>
            <Input
              id="att-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="att-company">Company (optional)</Label>
            <Input
              id="att-company"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="att-groups">Groups (optional)</Label>
            <Input
              id="att-groups"
              placeholder="SPEAKER, VIP DINNER, TELEMATICS"
              value={groupNamesText}
              onChange={(e) => setGroupNamesText(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Comma-separated. Meetpoint sync populates this automatically.
              Up to 32 entries, 200 characters each.
            </p>
          </div>
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? "Saving…" : attendee ? "Save" : "Add"}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
